// src/redux/lib/baseQueryWithReauth.ts
import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { AUTH_ENDPOINTS } from '../config/authApi.config';
import { tokenService } from '@/rtkQuery/features/services/auth/tokenService';
import { AuthService } from '../features/services/auth/authService';
import { getBaseApiUrl } from '@/utils/env.config';

type QueueItem = {
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    failedQueue = [];
};

const rawBaseQuery = fetchBaseQuery({
    baseUrl: getBaseApiUrl(),
    prepareHeaders: (headers, { endpoint }) => {
        const PUBLIC_ENDPOINTS = ['resetPassword', 'register', 'login', 'forgotPassword'];

        if (!PUBLIC_ENDPOINTS.includes(endpoint)) {
            const accessToken = tokenService.getAccessToken();
            if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
        }

        headers.set('Content-Type', 'application/json');
        return headers;
    }
});

export const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    if (result?.error?.status === 401) {
        const refreshToken = tokenService.getRefreshToken();
        const accessToken = tokenService.getAccessToken();
        if (!refreshToken || !tokenService.hasValidTokens()) {
            processQueue(new Error('No valid refresh token'));
            tokenService.clearTokens();
            return result;
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => rawBaseQuery(args, api, extraOptions))
                .catch((err) => ({ error: err } as any));
        }

        isRefreshing = true;

        try {
            const authService = new AuthService();
            const { success } = await authService.refreshToken(async () => rawBaseQuery(
                {
                    url: AUTH_ENDPOINTS.REFRESH,
                    method: 'POST',
                    body: {
                        accessToken,
                        refreshToken
                    },
                },
                api,
                extraOptions
            )
            );

            if (success) {
                processQueue(null);
                result = await rawBaseQuery(args, api, extraOptions);
            } else {
                throw new Error('Token refresh failed');
            }
        } catch (error) {
            processQueue(error);
        } finally {
            isRefreshing = false;
        }
    }

    return result;
};